import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {getPosterDuration} from '@Components/poster-editor/poster-editor-reducer';
import {ControlledSlider} from '@Components/controlled-slider';
import {onPosterPause} from '@Components/poster-editor/library/poster-editor-library';
import {HoverOverlay} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/hover-overlay/hover-overlay';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {useAppSelector} from '@/hooks';
import styles from './poster-bottom-bar-seekbar.module.scss';
import './poster-bottom-bar-seekbar.scss';

interface PosterBottomBarSeekbarProps {
  isOpenedInTimeline?: boolean;
}

export function PosterBottomBarSeekbar({isOpenedInTimeline = false}: PosterBottomBarSeekbarProps): ReactElement | null {
  const [showSeekbarHandle, setShowSeekbarHandle] = useState(false);
  const setCurrentTime = (newValue: number | number[]): void => {
    void window.posterEditor?.whiteboard?.seek(newValue as number);
  };
  const isExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const isPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });

  const currentPageTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const posterDuration = useAppSelector(getPosterDuration);
  const timelineHeight = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.timelineContentHeight;
  });
  const isDesignItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });
  const valueWhileResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.durationWhileResizing;
  });

  useEffect((): void => {
    if (isExpanded) {
      setTimeout((): void => {
        setShowSeekbarHandle(true);
      }, 200);
    } else {
      setShowSeekbarHandle(false);
    }
  });

  if (!posterDuration) {
    return null;
  }

  const getDividerHeight = (): undefined | number => {
    if (!timelineHeight) {
      return undefined;
    }

    return timelineHeight + 28 < 212 ? timelineHeight + 28 : 212;
  };

  const MAX_VALUE_FOR_SLIDER = isDesignItemResizing ? valueWhileResizing : posterDuration;

  return (
    <>
      <ClickableDiv
        onClick={(): void => {
          onPosterPause();
          setCurrentTime(0);
        }}
        className={`${styles.dummySliderTrack} ${isOpenedInTimeline ? styles.timelineDummyTrack : ''}`}
      />
      <HoverOverlay />
      <ControlledSlider
        className={`${styles.seekbar} ${isOpenedInTimeline ? styles.inTimeline : ''} poster-bottom-bar-seekbar`}
        value={currentPageTime > MAX_VALUE_FOR_SLIDER ? MAX_VALUE_FOR_SLIDER : currentPageTime}
        onSlide={(newVal): void => {
          onPosterPause();
          setCurrentTime(newVal);
        }}
        max={MAX_VALUE_FOR_SLIDER}
        step={0.01}
        showTooltipOnDrag
        handleStyle={{opacity: 0}}
        addDelayToChange={!isPlaying}
        showSeekDividerWithHandle={showSeekbarHandle}
        seekDividerClassName={styles.divider}
        dividerHeight={getDividerHeight()}
      />
    </>
  );
}
